import {
  createModelPricingCatalog
} from "./tokenCost.js";
function emptyUsage() {
  return {
    inputTokens: 0,
    outputTokens: 0,
    reasoningTokens: 0,
    cachedInputTokens: 0,
    cacheWriteTokens: 0
  };
}
function addUsageInto(target, add) {
  target.inputTokens += add.inputTokens;
  target.outputTokens += add.outputTokens;
  target.reasoningTokens += add.reasoningTokens;
  target.cachedInputTokens += add.cachedInputTokens;
  target.cacheWriteTokens += add.cacheWriteTokens;
}
function toUsageBuckets(usage) {
  return {
    inputTokens: usage.inputTokens,
    cachedInputTokens: usage.cachedInputTokens,
    cacheWriteTokens: usage.cacheWriteTokens,
    outputTokens: usage.outputTokens
  };
}
const openAIPricingByModel = {
  "gpt-6.1-sol": {
    costPer1mInputTokensUsd: 2,
    costPer1mOutputTokensUsd: 10,
    costPer1mCachedInputTokensUsd: 0.1,
    costPer1mCacheWriteTokensUsd: 2.5,
    costPer1mCacheWrite12hTokensUsd: 5
  },
  "gpt-6-astra": {
    costPer1mInputTokensUsd: 10,
    costPer1mOutputTokensUsd: 50,
    costPer1mCachedInputTokensUsd: 1,
    costPer1mCacheWriteTokensUsd: 12.5,
    costPer1mCacheWrite12hTokensUsd: 25
  },
  "gpt-6-sol": {
    costPer1mInputTokensUsd: 2,
    costPer1mOutputTokensUsd: 10,
    costPer1mCachedInputTokensUsd: 0.2,
    costPer1mCacheWriteTokensUsd: 2.5,
    costPer1mCacheWrite12hTokensUsd: 5
  },
  "gpt-6-luna": {
    costPer1mInputTokensUsd: 0.1,
    costPer1mOutputTokensUsd: 0.5,
    costPer1mCachedInputTokensUsd: 0.01,
    costPer1mCacheWriteTokensUsd: 0.125,
    costPer1mCacheWrite12hTokensUsd: 0.25
  },
  "gpt-5.6-sol": {
    costPer1mInputTokensUsd: 5,
    costPer1mOutputTokensUsd: 30,
    costPer1mCachedInputTokensUsd: 0.5,
    costPer1mCacheWriteTokensUsd: 6.25,
    costPer1mCacheWrite12hTokensUsd: 12.5
  },
  "gpt-5.6-terra": {
    costPer1mInputTokensUsd: 2,
    costPer1mOutputTokensUsd: 12,
    costPer1mCachedInputTokensUsd: 0.2,
    costPer1mCacheWriteTokensUsd: 2.5,
    costPer1mCacheWrite12hTokensUsd: 5
  },
  "gpt-5.6-luna": {
    costPer1mInputTokensUsd: 0.2,
    costPer1mOutputTokensUsd: 1.2,
    costPer1mCachedInputTokensUsd: 0.02,
    costPer1mCacheWriteTokensUsd: 0.25,
    costPer1mCacheWrite12hTokensUsd: 0.5
  },
  "gpt-5.5": {
    costPer1mInputTokensUsd: 5,
    costPer1mOutputTokensUsd: 30,
    costPer1mCachedInputTokensUsd: 0.5
  },
  // The pro models publish no cached-input rate of their own: they still serve
  // (and report) cache hits, they just carry no discount, so the read rate is
  // the ordinary input rate. Spelled out rather than left absent, because an
  // absent rate means "unknown" and records the spend as unpriced.
  "gpt-5.5-pro": {
    costPer1mInputTokensUsd: 30,
    costPer1mOutputTokensUsd: 180,
    costPer1mCachedInputTokensUsd: 30
  },
  "gpt-5.4": {
    costPer1mInputTokensUsd: 2.5,
    costPer1mOutputTokensUsd: 15,
    costPer1mCachedInputTokensUsd: 0.25
  },
  "gpt-5.4-mini": {
    costPer1mInputTokensUsd: 0.75,
    costPer1mOutputTokensUsd: 4.5,
    costPer1mCachedInputTokensUsd: 0.075
  },
  "gpt-5.4-nano": {
    costPer1mInputTokensUsd: 0.2,
    costPer1mOutputTokensUsd: 1.25,
    costPer1mCachedInputTokensUsd: 0.02
  },
  "gpt-5.4-pro": {
    costPer1mInputTokensUsd: 30,
    costPer1mOutputTokensUsd: 180,
    costPer1mCachedInputTokensUsd: 30
  },
  // OpenAI list price at GPT-5.2's launch, 2025-12-11.
  "gpt-5.2": {
    costPer1mInputTokensUsd: 1.75,
    costPer1mOutputTokensUsd: 14,
    costPer1mCachedInputTokensUsd: 0.175
  },
  // https://developers.openai.com/api/docs/models/gpt-realtime-2.1, checked
  // 2026-10-09. Image input ($5, $0.5 cached) has no bucket of its own and
  // bills at the text rates; nothing we run sends images.
  "gpt-realtime-2.1": {
    costPer1mInputTokensUsd: 4,
    costPer1mOutputTokensUsd: 24,
    costPer1mCachedInputTokensUsd: 0.4,
    audio: {
      costPer1mInputTokensUsd: 32,
      costPer1mCachedInputTokensUsd: 0.4,
      costPer1mOutputTokensUsd: 64
    }
  }
};
function toOpenAIBaseModel(model) {
  return model.replace(/-\d{4}-\d{2}-\d{2}$/, "");
}
const openAIModelPricing = createModelPricingCatalog(
  openAIPricingByModel,
  toOpenAIBaseModel
);
const OPENAI_PRICED_MODEL_IDS = openAIModelPricing.pricedModelIds;
function findOpenAIModelPricing(model) {
  return openAIModelPricing.findPricing(model);
}
function computeOpenAIModelCostUsd(model, usage) {
  return openAIModelPricing.computeCostUsd(model, usage);
}
export {
  OPENAI_PRICED_MODEL_IDS,
  addUsageInto,
  computeOpenAIModelCostUsd,
  emptyUsage,
  findOpenAIModelPricing,
  openAIModelPricing,
  toOpenAIBaseModel,
  toUsageBuckets
};
//# sourceMappingURL=openAITokenUsage.js.map
